/**
 * Department setup in one sitting (D3, workspace docs/guardian/09, 2026-10-23).
 * Admin only: a checklist, questions the Backend mined from the department's
 * own documents ("Your documents say Truck 14 times. What is a Truck?"), and a
 * knowledge check. Every answer creates a DRAFT that the admin approves in the
 * vocabulary / structure sections below. All text is rendered as text only.
 * A 503 `onboarding_unavailable` hides the section.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  answerOnboardingQuestion,
  dismissOnboardingQuestion,
  fetchDepartmentOnboarding,
  runKnowledgeCheck,
} from '../api/guardianApi';
import { departmentErrorMessage } from '../utils/departmentErrors';

export const CHECKLIST_TEXT = {
  area: 'Watched area set',
  documents: 'At least one document read',
  rules_drafted: 'Rules drafted from your documents',
  rules_reviewed: 'Every drafted rule reviewed',
  version_published: 'First version published',
  interlinked_review: 'Interlinked review done',
  vocabulary: 'Vocabulary started',
  stations_dispatch: 'Stations and dispatch center named',
  settings: 'Language settings chosen',
};

function QuestionCard({ q, unitTypes, onAnswer, onDismiss }) {
  const [meaning, setMeaning] = useState('');
  const [scope, setScope] = useState(q.kind === 'radio_code' ? 'radio' : 'both');
  const [canonicalId, setCanonicalId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const act = async (fn) => {
    setBusy(true);
    setError(null);
    const err = await fn();
    setBusy(false);
    if (err) setError(err);
  };
  const answer = () => {
    if (q.kind === 'callsign') return act(() => onAnswer(q, { ours: true }));
    const body = { meaning: meaning.trim(), scope };
    if (q.kind === 'unit_word' && canonicalId) body.canonicalId = canonicalId;
    return act(() => onAnswer(q, body));
  };
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="onboarding-question">
      <Typography variant="body1">{q.prompt}</Typography>
      {(q.excerpts || []).map((e) => (
        <Typography key={e} variant="caption" color="text.secondary" component="blockquote" sx={{ display: 'block', mt: 0.5 }}>
          “{e}”
        </Typography>
      ))}
      {q.kind !== 'callsign' && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          <TextField size="small" label="What it means" value={meaning} onChange={(e) => setMeaning(e.target.value)} inputProps={{ maxLength: 300 }} sx={{ flex: '1 1 240px' }} />
          <Select size="small" value={scope} onChange={(e) => setScope(e.target.value)} inputProps={{ 'aria-label': 'Used on' }}>
            <MenuItem value="radio">Radio</MenuItem>
            <MenuItem value="written">Written</MenuItem>
            <MenuItem value="both">Radio and written</MenuItem>
          </Select>
          {q.kind === 'unit_word' && (
            <Select size="small" displayEmpty value={canonicalId} onChange={(e) => setCanonicalId(e.target.value)} inputProps={{ 'aria-label': 'National unit type' }}>
              <MenuItem value="">National unit type (optional)</MenuItem>
              {unitTypes.map((u) => <MenuItem key={u.canonicalId} value={u.canonicalId}>{u.label}</MenuItem>)}
            </Select>
          )}
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button variant="contained" size="small" disabled={busy || (q.kind !== 'callsign' && !meaning.trim())} onClick={answer}>
          {q.kind === 'callsign' ? 'Yes, it’s ours' : 'Save as draft'}
        </Button>
        <Button size="small" disabled={busy} onClick={() => act(() => onDismiss(q))}>Not used here</Button>
      </Box>
    </Paper>
  );
}

export default function DepartmentSetup({ authToken, onChanged }) {
  const [data, setData] = useState(null);
  const [off, setOff] = useState(false);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    const res = await fetchDepartmentOnboarding(authToken);
    if (!alive.current) return;
    if (res?.success) { setData(res.data); setError(null); }
    else if (res?.status === 503) setOff(true);
    else setError(departmentErrorMessage(res));
  }, [authToken]);
  useEffect(() => { load(); }, [load]);

  const after = async (res) => {
    if (!res?.success) return departmentErrorMessage(res);
    await load();
    if (onChanged) onChanged();
    return null;
  };
  const onAnswer = async (q, body) => after(await answerOnboardingQuestion(authToken, q.questionKey, body));
  const onDismiss = async (q) => after(await dismissOnboardingQuestion(authToken, q.questionKey));
  const check = async () => {
    setChecking(true);
    const res = await runKnowledgeCheck(authToken);
    if (!alive.current) return;
    setChecking(false);
    if (res?.success) await load();
    else setError(departmentErrorMessage(res));
  };

  if (off) return null;
  if (!data) return error ? <Alert severity="error">{error}</Alert> : null;
  const kc = data.knowledgeCheck;
  const questions = data.questions || [];
  const done = (data.checklist || []).filter((c) => c.done).length;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} aria-labelledby="dept-setup-title">
      <Typography id="dept-setup-title" variant="h6" component="h2">Department setup</Typography>
      <Typography variant="body2" color="text.secondary">{done} of {(data.checklist || []).length} done</Typography>
      <Box component="ul" sx={{ listStyle: 'none', pl: 0, my: 1 }}>
        {(data.checklist || []).map((c) => (
          <li key={c.id}>
            <Chip size="small" color={c.done ? 'success' : 'default'} label={c.done ? 'Done' : 'To do'} sx={{ mr: 1 }} />
            {CHECKLIST_TEXT[c.id] || c.id}
          </li>
        ))}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Typography variant="subtitle1" component="h3" sx={{ mt: 2 }}>Questions from your documents</Typography>
      {questions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No open questions. New ones appear when you add documents.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {questions.map((q) => (
            <QuestionCard key={q.questionKey} q={q} unitTypes={data.neris?.unitTypes || []} onAnswer={onAnswer} onDismiss={onDismiss} />
          ))}
        </Box>
      )}
      <Typography variant="subtitle1" component="h3" sx={{ mt: 2 }}>Knowledge check</Typography>
      <Typography variant="body2" color="text.secondary">
        Guardian asks itself one question per published rule and lists the rules it could not find.
      </Typography>
      {kc && (
        <Box sx={{ mt: 1 }} data-testid="knowledge-check">
          <Typography variant="body2">
            Version {kc.version}: found {kc.hits} of {kc.rulesChecked} rules
            {kc.sampled ? ` (a sample of ${kc.rulesTotal})` : ''}{kc.partial ? ' (stopped early)' : ''}.
          </Typography>
          {(kc.misses || []).map((m) => (
            <Typography key={m.ruleKey} variant="caption" component="div" color="text.secondary">
              Not found: “{m.question}”
            </Typography>
          ))}
        </Box>
      )}
      {checking && <LinearProgress sx={{ mt: 1 }} />}
      <Button size="small" sx={{ mt: 1 }} disabled={checking} onClick={check}>Run knowledge check</Button>
    </Paper>
  );
}
